import { useState, useEffect } from "react";
import axios from "axios";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Clock, Calendar, Award, Users, Zap,
  CheckCircle2, ArrowRight, Headphones,
  Star, Target, Sparkles, Package,
  Rocket, Radio, Trophy, HeartHandshake, CreditCard, MapPin, Music2, Disc3
} from "lucide-react";
import { MentorTile } from "@/components/lessons/MentorTile";
import { WeekBreakdown } from "@/components/lessons/WeekBreakdown";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const INSTRUCTORS = [
  {
    slug: "lister",
    spotsKey: "lister",
    name: "Lister",
    realName: "Mason",
    role: "Techno / Hard Dance Producer · Mentor",
    badge: "MOST POPULAR",
    photo: "https://customer-assets.emergentagent.com/job_11b5f9da-5695-4228-9b97-e837295ecfda/artifacts/7pa3kirm_images%20%282%29.jpeg",
    tagline: "4-Week 1-on-1 Mentorship · Limited spots",
    course: "Ableton / FL Studio Production Course",
    bio: "Hey, I'm Mason, better known as Lister. Over the past decade I've dedicated my life to producing electronic music, refining my craft, and building a catalogue that has reached millions of listeners worldwide — 10+ years producing, 200K+ monthly listeners, releases on respected labels and shows across Australia.",
    facts: [
      { icon: Calendar, main: "4 Weeks", sub: "Duration" },
      { icon: Clock, main: "8 Hours", sub: "Total Training" },
      { icon: Users, main: "1-on-1", sub: "With Lister" },
      { icon: Headphones, main: "2 Hours", sub: "Per Session" },
    ],
    listTitle: "What's included",
    included: [
      "4 × 2-hour sessions with Lister",
      "Ableton Live OR FL Studio",
      "Session templates + sample packs + presets",
      "Mixing & mastering chains",
      "Label submission & release strategy",
      "Ongoing community + support",
    ],
    detailsLink: "#lister-course-details",
    priceLabel: "Investment",
    price: "$1,250",
    priceNote: "Complete 4-week course",
    cta: "/booking?course=ableton",
    ctaLabel: "Enrol With Lister",
    testId: "enrol-production-btn",
    accent: "from-dream-violet to-purple-600",
    border: "border-dream-violet/40",
    iconColor: "text-dream-violet",
    btn: "bg-dream-violet hover:bg-dream-violet-glow",
  },
  {
    slug: "alex-m",
    spotsKey: "alex_m",
    name: "Alex M",
    role: "Hardstyle / Rawstyle Producer · Mentor",
    badge: "8-WEEK DEEP DIVE",
    photo: "https://customer-assets-jt897jd0.emergentagent.net/job_11b5f9da-5695-4228-9b97-e837295ecfda/artifacts/e2e0b29492bb4d41_Screenshot%202026-10-09%20at%209.07.08%E2%80%AFAM.png",
    photoPos: "object-[center_15%]",
    tagline: "8-Week 1-on-1 Mentorship · Hardstyle / Rawstyle",
    course: "Ableton Live Production Course",
    bio: "Melbourne-based Hardstyle and Rawstyle producer, Alex M specialises in the harder end of the electronic spectrum but is more than proficient across any genre. His 8-week deep dive gives you double the studio time, exclusive resources and a free month in the Harder Class community.",
    facts: [
      { icon: Calendar, main: "8 Weeks", sub: "Duration" },
      { icon: Clock, main: "16 Hours", sub: "Total Training" },
      { icon: Users, main: "1-on-1", sub: "With Alex M" },
      { icon: Headphones, main: "2 Hours", sub: "Per Session" },
    ],
    listTitle: "What's included",
    included: [
      "8 × 2-hour sessions with Alex M",
      "Ableton Live training",
      "Hardstyle / Rawstyle specialty (all genres welcome)",
      "Exclusive sample packs",
      "Project templates + custom presets",
      "1 month FREE Harder Class community",
    ],
    detailsLink: "#alex-course-details",
    priceLabel: "Investment",
    price: "$2,499",
    priceNote: "Complete 8-week course",
    cta: "/booking?course=ableton_alex",
    ctaLabel: "Enrol With Alex M",
    testId: "enrol-alex-btn",
    accent: "from-red-500 via-rose-500 to-pink-600",
    border: "border-red-500/40",
    iconColor: "text-rose-400",
    btn: "bg-rose-600 hover:bg-rose-500",
  },
  {
    slug: "joel-petrika",
    name: "Joel Petrika",
    role: "DJ · Mentor",
    badge: "DJ LESSONS",
    photo: "https://customer-assets.emergentagent.com/job_11b5f9da-5695-4228-9b97-e837295ecfda/artifacts/qcqgnkt9_IMG_0272.jpg",
    tagline: "1-on-1 DJ Lessons · All levels",
    course: "DJ Lessons",
    bio: "Working DJ and mentor at Dream Makers, Joel guides beginners through their first mix all the way to intermediate DJs sharpening their sets. Expect hands-on training on 3× Pioneer CDJ-3000s and a DJM-A9 in our sound-treated DJ Room.",
    facts: [
      { icon: Clock, main: "1.5 Hours", sub: "Per Session" },
      { icon: Zap, main: "Flexible", sub: "Scheduling" },
      { icon: Users, main: "All Levels", sub: "Welcome" },
      { icon: Headphones, main: "CDJ-3000", sub: "DJM-A9" },
    ],
    listTitle: "You'll learn",
    included: [
      "Beat-matching & mixing techniques",
      "Reading the crowd",
      "Building energy & flow",
      "Equipment mastery",
      "Genre-specific skills",
      "Pay per session — no lock-in",
    ],
    priceLabel: "Per Session",
    price: "$150",
    priceNote: "1.5-hour session",
    cta: "/booking?course=dj",
    ctaLabel: "Book With Joel",
    testId: "enrol-dj-btn",
    accent: "from-cyan-500 to-blue-600",
    border: "border-cyan-500/40",
    iconColor: "text-cyan-400",
    btn: "bg-cyan-600 hover:bg-cyan-500",
  },
];

const GOAL_POINTS = [
  "A deeper understanding of music production",
  "Better workflow and efficiency",
  "Improved mixing and mastering skills",
  "Stronger arrangements and sound design",
  "A clearer artistic direction",
  "Professional production resources to keep",
  "Valuable industry knowledge",
  "A support network of producers and artists",
  "The confidence to finish and release music",
];

const AUDIENCES = [
  "Beginner producers wanting a strong foundation",
  "Intermediate producers feeling stuck",
  "DJs wanting to create original music",
  "Artists wanting professional guidance",
  "Producers struggling to finish tracks",
  "Anyone wanting to release better music",
];

const WEEKS = [
  {
    week: 1,
    title: "Foundations & Workflow",
    icon: Rocket,
    topics: [
      "Ableton Live or FL Studio fundamentals",
      "Workflow optimisation",
      "Project organisation",
      "Drum programming",
      "Building professional habits",
      "Creating stronger ideas faster",
    ],
  },
  {
    week: 2,
    title: "Sound Design & Arrangement",
    icon: Radio,
    topics: [
      "Modern hard techno & multi-genre sound design",
      "VST synth creation",
      "Bass design",
      "Creating powerful drops",
      "Building tension and energy",
      "Arrangement techniques used by professionals",
    ],
  },
  {
    week: 3,
    title: "Production Secrets",
    icon: Sparkles,
    topics: [
      "Layering sounds correctly",
      "Processing chains",
      "Advanced automation",
      "Creating movement and excitement",
      "Professional effects usage",
      "Making tracks sound bigger and louder",
    ],
  },
  {
    week: 4,
    title: "Mixing, Mastering & Release Prep",
    icon: Trophy,
    topics: [
      "Mixing workflows",
      "EQ and compression techniques",
      "Loudness and clarity",
      "Mastering fundamentals",
      "Exporting tracks correctly",
      "Preparing music for labels and releases",
    ],
  },
];

const BONUS_GROUPS = [
  {
    title: "Production Resources",
    icon: Package,
    items: [
      "Professional Ableton & FL Studio session templates",
      "Custom project files",
      "Exclusive sample packs",
      "Drum samples & one-shots",
      "Presets & sound-design resources",
      "Mixing & mastering chains",
      "Workflow shortcuts & cheat sheets",
    ],
  },
  {
    title: "Artist Development & Career",
    icon: Target,
    items: [
      "One-on-one feedback",
      "Artist development coaching",
      "Branding & release advice",
      "Label submission guidance",
      "Industry insights",
      "Track reviews & breakdown sessions",
      "Career direction & goal setting",
    ],
  },
  {
    title: "Dream Makers Member Benefits",
    icon: Award,
    items: [
      "Discounts across Dream Makers services",
      "Discounted studio sessions",
      "Discounted mixing & mastering",
      "Discounted photoshoots & content creation",
      "Priority access to future workshops",
      "Exclusive member opportunities",
    ],
  },
  {
    title: "Community & Ongoing Support",
    icon: HeartHandshake,
    items: [
      "Producer community access",
      "Networking opportunities",
      "Feedback from other producers",
      "Accountability & motivation",
      "Ongoing support from Lister",
      "Continued guidance after the course",
    ],
  },
];

const stickerFor = (m, spots) => {
  if (!m.spotsKey) return { text: "Open availability · book anytime", tone: "open" };
  if (!spots) return null;
  const n = spots[m.spotsKey] ?? 0;
  if (n <= 0) return { text: "Fully booked this month", tone: "full" };
  return { text: `Only ${n} spot${n === 1 ? "" : "s"} available this month`, tone: "limited" };
};

const LISTER_THEME = {
  text: "text-dream-violet",
  iconBg: "bg-dream-violet/20",
  activeTab: "bg-dream-violet text-white border-dream-violet shadow-lg shadow-dream-violet/30",
  hoverBorder: "hover:border-dream-violet/50",
};

const ALEX_THEME = {
  text: "text-rose-400",
  iconBg: "bg-rose-500/20",
  activeTab: "bg-rose-600 text-white border-rose-500 shadow-lg shadow-rose-600/30",
  hoverBorder: "hover:border-rose-500/50",
};

const ALEX_WEEKS = [
  { week: 1, title: "Ableton Foundations & Workflow", icon: Rocket, topics: [
    "Ableton Live interface, Session & Arrangement view", "Project templates & organisation", "Hardstyle tempo, structure & key fundamentals",
    "Shortcuts & workflow speed", "Building your first hard dance loop", "Setting your goals for the 8 weeks",
  ] },
  { week: 2, title: "The Hardstyle Kick", icon: Zap, topics: [
    "Kick anatomy: punch, crunch & tail", "Layering & distortion chains", "Tuning the kick to your track key",
    "Euphoric vs Rawstyle kick design", "Using the included kick presets", "Kick & bass mono compatibility",
  ] },
  { week: 3, title: "Drums, Groove & Percussion", icon: Disc3, topics: [
    "Drum programming for harder styles", "Snares, claps & hats that cut through", "Percussion loops & fills",
    "Groove, swing & velocity", "Sidechain & ducking", "Building energy with drum rolls",
  ] },
  { week: 4, title: "Synths & Sound Design", icon: Radio, topics: [
    "Euphoric leads & supersaws", "Rawstyle screeches & atonal sounds", "Synth sound design from scratch",
    "Resampling techniques", "FX, risers & impacts", "Building your own preset bank",
  ] },
  { week: 5, title: "Melody, Harmony & Breakdowns", icon: Music2, topics: [
    "Chord progressions for euphoric hardstyle", "Writing memorable melodies", "Building emotional breakdowns",
    "Vocals & vocal chops", "Pads & atmospheres", "Transitioning back into the drop",
  ] },
  { week: 6, title: "Arrangement & Energy", icon: Target, topics: [
    "Intro, breakdown, climax & drop structure", "DJ-friendly intros & outros", "Tension, build-ups & anti-climaxes",
    "Automation for movement", "Reference track analysis", "Finishing a full arrangement",
  ] },
  { week: 7, title: "Mixing for Harder Styles", icon: Headphones, topics: [
    "Gain staging", "EQ & making room for the kick", "Bus processing & parallel compression",
    "Reverb & delay without the mud", "Stereo width & mono checks", "Loudness without killing dynamics",
  ] },
  { week: 8, title: "Mastering & Release Prep", icon: Trophy, topics: [
    "Mastering chains for hard dance", "Club-ready loudness", "Exporting stems & masters",
    "Label submissions & demo etiquette", "Promo & release strategy", "Onboarding into the Harder Class community",
  ] },
];

export default function DJLessons() {
  const [spots, setSpots] = useState(null);

  const { hash } = useLocation();
  useEffect(() => {
    if (!hash) return;
    const t = setTimeout(() => document.querySelector(hash)?.scrollIntoView({ behavior: "smooth" }), 300);
    return () => clearTimeout(t);
  }, [hash]);

  useEffect(() => {
    axios.get(`${API}/course-spots`).then((r) => setSpots(r.data)).catch(() => setSpots(null));
  }, []);

  const mentors = INSTRUCTORS.map((m) => ({ ...m, sticker: stickerFor(m, spots) }));

  return (
    <div className="pt-20 min-h-screen bg-dream-bg" data-testid="dj-production-lessons-page">
      {/* HERO */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-dream-violet/20 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp} className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-dream-violet/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4 text-dream-violet" />
              <span className="text-dream-violet text-sm font-medium">Practice · Create · Release</span>
            </div>

            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold text-white mb-6 leading-tight">
              DJ &amp; Production
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-dream-violet to-cyan-400">
                Lessons
              </span>
            </h1>

            <p className="text-white/60 text-lg md:text-xl mb-8 max-w-2xl mx-auto">
              Stop watching endless tutorials. Learn directly from working industry producers and DJs in Melbourne&apos;s most affordable professional studio.
            </p>

            <div className="grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto text-left" data-testid="mentor-quick-picks">
              {mentors.map((m) => (
                <a
                  key={m.slug}
                  href={`#mentor-${m.slug}`}
                  className={`group flex flex-col p-4 rounded-2xl bg-white/[0.03] border ${m.border} hover:bg-white/[0.06] transition-colors`}
                  data-testid={`quick-pick-${m.slug}`}
                >
                  <p className={`text-xs font-mono uppercase tracking-wider ${m.iconColor}`}>{m.name}</p>
                  <p className="text-white font-semibold text-sm mt-1 leading-snug">{m.course}</p>
                  {m.sticker && <p className={`text-[11px] font-semibold mt-2 ${m.sticker.tone === "open" ? "text-emerald-400" : m.sticker.tone === "full" ? "text-red-400" : "text-yellow-400"}`} data-testid={`quick-pick-${m.slug}-sticker`}>{m.sticker.text}</p>}
                  <div className="flex items-center justify-between mt-auto pt-3">
                    <span className="font-heading text-xl font-bold text-white">{m.price}</span>
                    <span className="text-white/40 text-xs">{m.priceNote}</span>
                  </div>
                </a>
              ))}
            </div>

            <div className="mt-10 inline-flex flex-wrap items-center justify-center gap-3 px-5 py-3 rounded-full bg-white/[0.03] border border-white/10">
              <CreditCard className="w-4 h-4 text-dream-violet" />
              <span className="text-white/80 text-sm font-medium">Afterpay &amp; Zip Pay available</span>
              <span className="text-white/40 text-xs">·</span>
              <span className="text-green-400 text-xs font-semibold">4 × 0% interest instalments</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* MENTORS + THEIR COURSES */}
      <section id="mentors" className="py-24 bg-dream-surface scroll-mt-20" data-testid="instructors-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="mb-14 max-w-3xl">
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Choose Your Mentor</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">Learn From Working Pros</h2>
            <p className="text-white/60">
              Every mentor is an active artist. Each card below shows the full deal — who you&apos;ll learn with, how long, what&apos;s included and exactly what it costs. No hidden fees.
            </p>
          </motion.div>
          <div className="space-y-10">
            {mentors.map((m, idx) => <MentorTile key={m.slug} m={m} idx={idx} />)}
          </div>
        </div>
      </section>

      {/* LISTER COURSE DETAILS DIVIDER */}
      <section id="lister-course-details" className="pt-24 pb-4 bg-dream-surface scroll-mt-20" data-testid="lister-course-details">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 p-8 rounded-3xl border border-dream-violet/30 bg-gradient-to-br from-dream-violet/15 to-transparent">
            <div>
              <p className="text-dream-violet font-mono text-xs tracking-widest uppercase mb-2">Lister&apos;s Course · In Detail</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Ableton / FL Studio Production Course</h2>
              <p className="text-white/60 text-sm mt-2">4 weeks · 8 hours · 2 hours per week · 1-on-1 · $1,250</p>
            </div>
            <Link to="/booking?course=ableton">
              <Button className="bg-dream-violet hover:bg-dream-violet-glow text-white rounded-full px-6 py-5 font-bold" data-testid="lister-details-enrol-btn">
                Enrol With Lister <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT LISTER WILL HELP YOU DO */}
      <section className="py-24 bg-dream-surface" data-testid="lister-goals-section">
        <div className="max-w-6xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="mb-14 max-w-3xl">
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Lister&apos;s Course · Why This Mentorship</p>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-6">
              Ready to stop watching endless tutorials and finally make music that sounds professional?
            </h2>
            <p className="text-white/70 leading-relaxed">
              Lister opens the door for producers who want more than just tutorials. He&apos;ll help you break through creative plateaus, finish more music, build confidence in your productions, improve your mixdowns, develop your own unique sound, understand how the industry actually works, and create music that can compete at a professional level.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              "Break through creative plateaus",
              "Finish more music",
              "Build confidence in your productions",
              "Improve your mixdowns",
              "Develop your own unique sound",
              "Understand how the industry actually works",
              "Compete at a professional level",
            ].map((point, i) => (
              <motion.div
                key={point}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                className="flex items-start gap-3 p-4 rounded-xl bg-white/[0.03] border border-white/10 hover:border-dream-violet/40 transition-colors"
              >
                <div className="w-6 h-6 rounded-full bg-dream-violet/20 flex-shrink-0 flex items-center justify-center mt-0.5">
                  <CheckCircle2 className="w-4 h-4 text-dream-violet" />
                </div>
                <span className="text-white/80 text-sm">{point}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* LISTER WEEK BREAKDOWN */}
      <WeekBreakdown
        weeks={WEEKS}
        eyebrow="Lister's Course · The Journey"
        title="What You'll Learn With Lister"
        subtitle="4 weeks · 8 hours · 2 hours per week · 1-on-1 with Lister."
        mentor="Lister"
        theme={LISTER_THEME}
      />

      {/* EXCLUSIVE BONUSES */}
      <section className="py-24 bg-gradient-to-br from-dream-violet/10 via-purple-900/5 to-cyan-500/5" data-testid="bonuses-section">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} className="text-center mb-16">
            <div className="inline-flex items-center gap-2 px-3 py-1 mb-4 rounded-full bg-yellow-500/10 border border-yellow-500/30">
              <Award className="w-4 h-4 text-yellow-400" />
              <span className="text-xs font-mono uppercase tracking-widest text-yellow-400">Lister&apos;s Course · Bonuses Included</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">More Than Just Lessons</h2>
            <p className="text-white/60 max-w-2xl mx-auto">
              When you join Lister&apos;s mentorship you also get access to resources, tools, and support designed to help you keep growing long after the course finishes.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {BONUS_GROUPS.map((g, i) => {
              const GIcon = g.icon;
              return (
                <motion.div
                  key={g.title}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeInUp}
                  transition={{ delay: i * 0.08 }}
                  className="p-7 rounded-2xl bg-dream-surface/40 border border-white/10"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-11 h-11 rounded-xl bg-dream-violet/15 flex items-center justify-center">
                      <GIcon className="w-5 h-5 text-dream-violet" />
                    </div>
                    <h3 className="font-heading text-xl font-bold text-white">{g.title}</h3>
                  </div>
                  <ul className="space-y-2.5">
                    {g.items.map((item) => (
                      <li key={item} className="flex items-start gap-2.5 text-white/75 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHO IS THIS FOR + THE GOAL */}
      <section className="py-24 bg-dream-bg">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Lister&apos;s Course · Who This Is For</p>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">Built for producers who are serious about their craft.</h3>
            <ul className="space-y-3">
              {AUDIENCES.map((a) => (
                <li key={a} className="flex items-start gap-3 text-white/80">
                  <Star className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp} transition={{ delay: 0.1 }}>
            <p className="text-dream-violet font-mono text-sm tracking-widest mb-4 uppercase">Lister&apos;s Course · The Goal</p>
            <h3 className="font-heading text-2xl md:text-3xl font-bold text-white mb-6">By the end of this mentorship you&apos;ll have:</h3>
            <ul className="space-y-3">
              {GOAL_POINTS.map((g) => (
                <li key={g} className="flex items-start gap-3 text-white/80">
                  <CheckCircle2 className="w-5 h-5 text-dream-violet flex-shrink-0 mt-0.5" />
                  <span>{g}</span>
                </li>
              ))}
            </ul>
            <div className="mt-8 p-5 rounded-2xl bg-gradient-to-br from-dream-violet/10 to-cyan-500/5 border border-dream-violet/20">
              <p className="text-white/85 leading-relaxed">
                <strong>This isn&apos;t just a course.</strong> It&apos;s an investment into your growth as a producer, your future as an artist, and your ability to create music at a level you&apos;re truly proud of.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ALEX M COURSE DETAILS */}
      <section id="alex-course-details" className="pt-24 pb-4 bg-dream-surface scroll-mt-20" data-testid="alex-course-details">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 p-8 rounded-3xl border border-rose-500/30 bg-gradient-to-br from-rose-500/15 to-transparent">
            <div>
              <p className="text-rose-400 font-mono text-xs tracking-widest uppercase mb-2">Alex M&apos;s Course · In Detail</p>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-white">Ableton Live Production Course</h2>
              <p className="text-white/60 text-sm mt-2">8 weeks · 16 hours · 2 hours per week · 1-on-1 · $2,499 · 1 month FREE Harder Class</p>
            </div>
            <Link to="/booking?course=ableton_alex">
              <Button className="bg-rose-600 hover:bg-rose-500 text-white rounded-full px-6 py-5 font-bold" data-testid="alex-details-enrol-btn">
                Enrol With Alex M <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>
      <WeekBreakdown
        weeks={ALEX_WEEKS}
        eyebrow="Alex M's Course · The Journey"
        title="What You'll Learn With Alex M"
        subtitle="8 weeks · 16 hours · 2 hours per week · 1-on-1 with Alex M. Hardstyle / Rawstyle focus — all genres welcome."
        mentor="Alex M"
        theme={ALEX_THEME}
        testPrefix="alex-"
        bg="bg-dream-surface"
      />

      {/* LOCATION + CTA */}
      <section className="py-24 bg-gradient-to-r from-dream-violet/20 via-purple-500/20 to-cyan-500/20" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-2 mb-6 rounded-full bg-white/[0.05] border border-white/10">
              <MapPin className="w-4 h-4 text-dream-violet" />
              <span className="text-white/80 text-sm">Hosted at Dream Makers Recording Group · Tullamarine, Melbourne</span>
            </div>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">Ready to Level Up?</h2>
            <p className="text-white/70 text-lg mb-4 max-w-2xl mx-auto">
              Limited positions available to ensure every student receives personalised attention. Payment plans available via Afterpay &amp; Zip Pay.
            </p>
            <p className="text-white/50 text-sm mb-8">
              Not sure who to pick? Call us and we&apos;ll match you with the right mentor.
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap gap-4 justify-center">
              <Link to="/booking?course=ableton">
                <Button className="bg-white text-dream-bg hover:bg-dream-violet hover:text-white rounded-full px-7 py-6 text-base font-bold" data-testid="cta-enrol-btn">
                  Lister · $1,250
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/booking?course=ableton_alex">
                <Button className="bg-white text-dream-bg hover:bg-rose-600 hover:text-white rounded-full px-7 py-6 text-base font-bold" data-testid="cta-enrol-alex-btn">
                  Alex M · $2,499
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
              <Link to="/booking?course=dj">
                <Button
                  variant="outline"
                  className="border-white/30 text-white hover:border-white rounded-full px-7 py-6 text-base bg-transparent"
                  data-testid="cta-book-dj-btn"
                >
                  Joel · DJ Lesson $150
                </Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/50 text-sm">
              <span className="flex items-center gap-2"><CreditCard className="w-4 h-4 text-dream-violet" /> Afterpay &amp; Zip Pay</span>
              <span className="flex items-center gap-2"><HeartHandshake className="w-4 h-4 text-dream-violet" /> 1-on-1 mentorship</span>
              <a href="tel:0421815593" className="hover:text-white transition-colors">Call 0421 815 593</a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
